// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * render.ts — the map, and everything that makes it feel cold.
 *
 * Canvas rather than DOM for the board: the warmth gradient, the drifting snow,
 * the frost creeping over a tile the ring has left, and the ember spray off a
 * stoke are all things a canvas does in one pass and a DOM would fight. The
 * HUD, tray and every screen stay DOM (ui.ts), where hit targets and
 * accessibility come free.
 *
 * Every colour comes from palette.ts. There are no colour literals in this file
 * and there must never be: principle #22 exists because a piece drawn in the
 * right cell at the right size, in a colour close to its background, is
 * invisible AND passes every geometry gate. The only way that gets caught is by
 * measuring the palette, and the palette can only be measured if it is all in
 * one place.
 */

import { Game, type BuildingKind, type Tile } from './game';
import { corners, fromOrigin, fromPixel, toPixel } from './hex';
import { PALETTE, TERRAIN, TERRAIN_FROZEN } from './palette';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  colour: string;
  text?: string;
}

interface Flake {
  x: number;
  y: number;
  vy: number;
  drift: number;
  size: number;
  phase: number;
}

export interface Renderer {
  resize(): void;
  draw(dt: number): void;
  /** Screen-space point -> the cell under it, or null if off the map. */
  cellAt(clientX: number, clientY: number): { q: number; r: number } | null;
  /** Cell -> screen-space centre, for anchoring DOM affordances. */
  centreOf(q: number, r: number): { x: number; y: number };
  hexSize(): number;
  shake(power: number): void;
  puff(q: number, r: number, colour: string, count?: number): void;
  float(q: number, r: number, text: string, colour: string): void;
  /** Ring pulse when the cold bites or the generator is stoked. */
  pulse(inward: boolean): void;
  /** Tile the player is dragging a building over, or null. */
  setHover(cell: { q: number; r: number } | null, ok: boolean): void;
  destroy(): void;
}

const reduced = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function createRenderer(canvas: HTMLCanvasElement, getGame: () => Game | null): Renderer {
  const ctx = canvas.getContext('2d');
  let size = 24;
  let ox = 0;
  let oy = 0;
  let w = 0;
  let h = 0;

  const particles: Particle[] = [];
  const flakes: Flake[] = [];
  let shakeAmt = 0;
  let ringPulse = 0;
  let ringPulseDir = 1;
  let t = 0;
  let hover: { q: number; r: number } | null = null;
  let hoverOk = false;
  const soft = reduced();

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    // A transient 0-size measurement (hidden tab, mid-layout) would produce a
    // NaN/Infinity scale and silently drop every input for the rest of the
    // session. Ignore it and let the next frame retry.
    if (rect.width < 2 || rect.height < 2) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    w = rect.width;
    h = rect.height;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const g = getGame();
    const radius = g?.mode.radius ?? 4;
    const pad = 10;
    // Solve the hex size that makes the disc fit both axes.
    const byW = (w - pad * 2) / (Math.sqrt(3) * (2 * radius + 1));
    const byH = (h - pad * 2) / (1.5 * 2 * radius + 2);
    size = Math.max(8, Math.min(byW, byH));
    ox = w / 2;
    oy = h / 2;

    if (flakes.length === 0) seedSnow();
  }

  function seedSnow(): void {
    const n = soft ? 24 : 90;
    for (let i = 0; i < n; i++) {
      flakes.push({
        x: Math.random() * Math.max(1, w),
        y: Math.random() * Math.max(1, h),
        vy: 8 + Math.random() * 26,
        drift: -14 + Math.random() * 28,
        size: 0.7 + Math.random() * 1.7,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  const centreOf = (q: number, r: number): { x: number; y: number } => {
    const p = toPixel(q, r, size);
    return { x: ox + p.x, y: oy + p.y };
  };

  function hexPath(cx: number, cy: number, s: number): void {
    if (!ctx) return;
    const pts = corners(cx, cy, s);
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < 6; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
  }

  function drawFrost(cx: number, cy: number, s: number): void {
    if (!ctx) return;
    // Ice crystals, not a tint: this overlay is what actually tells the player
    // a tile is dead, because no single tint can shift the darkest and the
    // lightest terrain by the same visible amount. See palette.ts / contrast.
    ctx.strokeStyle = PALETTE.frost;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = Math.max(1, s * 0.055);
    ctx.lineCap = 'round';
    const arm = s * 0.3;
    for (const [dx, dy] of [
      [-0.42, -0.3],
      [0.38, 0.34],
      [0.4, -0.36],
    ]) {
      const px = cx + dx * s;
      const py = cy + dy * s;
      for (let i = 0; i < 3; i++) {
        const a = (Math.PI / 3) * i;
        ctx.beginPath();
        ctx.moveTo(px - Math.cos(a) * arm * 0.5, py - Math.sin(a) * arm * 0.5);
        ctx.lineTo(px + Math.cos(a) * arm * 0.5, py + Math.sin(a) * arm * 0.5);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawBuilding(kind: BuildingKind, cx: number, cy: number, s: number, lit: boolean): void {
    if (!ctx) return;
    const r = s * 0.46;
    // Shapes differ as well as colours, so the buildings are still tellable
    // apart without colour vision at all.
    ctx.save();
    ctx.translate(cx, cy);
    if (!lit) ctx.globalAlpha = 0.82;
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(1.5, s * 0.08);
    ctx.strokeStyle = PALETTE.ringCasing;

    ctx.beginPath();
    if (kind === 'home') {
      // A house: gable over a body.
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.86, -r * 0.1);
      ctx.lineTo(r * 0.62, -r * 0.1);
      ctx.lineTo(r * 0.62, r * 0.78);
      ctx.lineTo(-r * 0.62, r * 0.78);
      ctx.lineTo(-r * 0.62, -r * 0.1);
      ctx.lineTo(-r * 0.86, -r * 0.1);
      ctx.closePath();
      ctx.fillStyle = PALETTE.home;
    } else if (kind === 'hut') {
      // A dome, flat on the ground.
      ctx.arc(0, r * 0.2, r * 0.8, Math.PI, 0);
      ctx.lineTo(r * 0.8, r * 0.5);
      ctx.lineTo(-r * 0.8, r * 0.5);
      ctx.closePath();
      ctx.fillStyle = PALETTE.hut;
    } else {
      // A cog: square on point, so its silhouette is unmistakable.
      const k = r * 0.85;
      ctx.moveTo(0, -k);
      ctx.lineTo(k, 0);
      ctx.lineTo(0, k);
      ctx.lineTo(-k, 0);
      ctx.closePath();
      ctx.fillStyle = PALETTE.workshop;
    }
    ctx.stroke();
    ctx.fill();
    ctx.restore();
  }

  function drawGenerator(cx: number, cy: number, s: number, heat: number): void {
    if (!ctx) return;
    const beat = soft ? 1 : 1 + Math.sin(t * 2.2) * 0.05;
    const r = s * 0.5 * beat;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.lineWidth = Math.max(1.5, s * 0.08);
    ctx.strokeStyle = PALETTE.ringCasing;
    ctx.fillStyle = PALETTE.generator;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // The last light itself — brighter the more heat is left, so the generator
    // visibly dims as the run gets away from you.
    const lit = Math.max(0.25, Math.min(1, heat / 3));
    ctx.globalAlpha = lit;
    ctx.fillStyle = PALETTE.core;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.42, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function drawWarmth(g: Game): void {
    if (!ctx) return;
    const ring = g.warmth;
    // The glow. Its radius follows the ring so the light and the rule agree.
    const reach = (ring + 0.62) * size * 1.732;
    const grad = ctx.createRadialGradient(ox, oy, size * 0.2, ox, oy, Math.max(1, reach));
    grad.addColorStop(0, 'rgba(255,176,92,0.30)');
    grad.addColorStop(0.55, 'rgba(240,138,60,0.13)');
    grad.addColorStop(1, 'rgba(240,138,60,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // The boundary, drawn as the outer edges of the warm cells — exact, because
    // on a hex grid a circle of constant distance IS a ring of cells.
    const edges: Array<[number, number, number, number]> = [];
    for (const tile of g.tiles.values()) {
      if (fromOrigin(tile.q, tile.r) !== ring) continue;
      const c = centreOf(tile.q, tile.r);
      const pts = corners(c.x, c.y, size);
      for (let i = 0; i < 6; i++) {
        // Edge i sits between corner i and i+1; the neighbour across it is
        // DIRECTIONS[(i + 4) % 6] for a pointy-top layout.
        const dirs = [
          [1, 0],
          [0, 1],
          [-1, 1],
          [-1, 0],
          [0, -1],
          [1, -1],
        ];
        const d = dirs[i];
        if (fromOrigin(tile.q + d[0], tile.r + d[1]) <= ring) continue;
        const a = pts[i];
        const b = pts[(i + 1) % 6];
        edges.push([a[0], a[1], b[0], b[1]]);
      }
    }

    const pulse = 1 + ringPulse * 0.5 * ringPulseDir;
    // Casing first, then the line on top, so the ring never has to win a
    // contrast fight against whatever terrain is under it.
    for (const [pass, colour, mul] of [
      [0, PALETTE.ringCasing, 2.0],
      [1, PALETTE.ring, 1.0],
    ] as const) {
      ctx.strokeStyle = colour;
      ctx.lineWidth = Math.max(pass === 0 ? 3 : 1.6, size * 0.1 * mul * pulse);
      ctx.lineCap = 'round';
      ctx.beginPath();
      for (const [x1, y1, x2, y2] of edges) {
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      }
      ctx.stroke();
    }
  }

  function drawTile(g: Game, tile: Tile): void {
    if (!ctx) return;
    const c = centreOf(tile.q, tile.r);
    const lit = g.inWarmth(tile.q, tile.r);
    hexPath(c.x, c.y, size * 0.955);

    if (tile.fogged) {
      ctx.fillStyle = PALETTE.fog;
      ctx.fill();
      ctx.strokeStyle = PALETTE.gridline;
      ctx.lineWidth = 1;
      ctx.stroke();
      return;
    }

    ctx.fillStyle = lit ? TERRAIN[tile.terrain] : TERRAIN_FROZEN[tile.terrain];
    ctx.fill();
    ctx.strokeStyle = PALETTE.gridline;
    ctx.lineWidth = 1;
    ctx.stroke();

    if (!lit) drawFrost(c.x, c.y, size);

    if (tile.q === 0 && tile.r === 0) {
      drawGenerator(c.x, c.y, size, g.heat);
    } else if (tile.building) {
      drawBuilding(tile.building, c.x, c.y, size, lit);
    }
  }

  function drawScouts(g: Game): void {
    if (!ctx) return;
    for (const s of g.scouts) {
      const target = centreOf(s.q, s.r);
      // A dashed thread from the generator to wherever they went, so a scout in
      // the fog is a visible commitment rather than a forgotten one.
      ctx.save();
      ctx.strokeStyle = PALETTE.scout;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = Math.max(1, size * 0.05);
      ctx.setLineDash([size * 0.16, size * 0.18]);
      ctx.lineDashOffset = soft ? 0 : -t * 22;
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.lineTo(target.x, target.y);
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.fillStyle = PALETTE.scout;
      ctx.strokeStyle = PALETTE.ringCasing;
      ctx.lineWidth = Math.max(1.2, size * 0.06);
      ctx.beginPath();
      ctx.arc(target.x, target.y, size * 0.24, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }

  function drawHover(): void {
    if (!ctx || !hover) return;
    const c = centreOf(hover.q, hover.r);
    hexPath(c.x, c.y, size * 0.955);
    ctx.strokeStyle = hoverOk ? PALETTE.good : PALETTE.danger;
    ctx.lineWidth = Math.max(2, size * 0.1);
    ctx.stroke();
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = hoverOk ? PALETTE.good : PALETTE.danger;
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  function stepParticles(dt: number): void {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 42 * dt;
      p.vx *= 0.98;
    }
  }

  function drawParticles(): void {
    if (!ctx) return;
    for (const p of particles) {
      const a = Math.max(0, Math.min(1, p.life / p.maxLife));
      ctx.globalAlpha = a;
      if (p.text) {
        ctx.fillStyle = p.colour;
        ctx.font = `700 ${Math.round(p.size)}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.strokeStyle = PALETTE.ringCasing;
        ctx.lineWidth = 3;
        ctx.strokeText(p.text, p.x, p.y);
        ctx.fillText(p.text, p.x, p.y);
      } else {
        ctx.fillStyle = p.colour;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * a, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawSnow(dt: number, g: Game | null): void {
    if (!ctx) return;
    ctx.fillStyle = PALETTE.frost;
    for (const f of flakes) {
      if (!soft) {
        f.y += f.vy * dt;
        f.x += Math.sin(t * 0.7 + f.phase) * f.drift * dt;
        if (f.y > h + 4) {
          f.y = -4;
          f.x = Math.random() * w;
        }
        if (f.x < -4) f.x = w + 4;
        if (f.x > w + 4) f.x = -4;
      }
      // Snow thins out inside the warmth — the ring is visibly holding it off.
      let a = 0.5;
      if (g) {
        const d = Math.hypot(f.x - ox, f.y - oy);
        const reach = (g.warmth + 0.6) * size * 1.732;
        if (d < reach) a = 0.14;
      }
      ctx.globalAlpha = a;
      ctx.beginPath();
      ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  function draw(dt: number): void {
    if (!ctx) return;
    if (w < 2 || h < 2) resize();
    t += dt;

    ctx.setTransform(
      Math.min(3, window.devicePixelRatio || 1),
      0,
      0,
      Math.min(3, window.devicePixelRatio || 1),
      0,
      0,
    );
    ctx.fillStyle = PALETTE.ground;
    ctx.fillRect(0, 0, w, h);

    if (shakeAmt > 0) {
      shakeAmt = Math.max(0, shakeAmt - dt * 26);
      if (!soft) {
        ctx.translate((Math.random() - 0.5) * shakeAmt, (Math.random() - 0.5) * shakeAmt);
      }
    }
    if (ringPulse > 0) ringPulse = Math.max(0, ringPulse - dt * 2.6);

    const g = getGame();
    if (g) {
      for (const tile of g.tiles.values()) drawTile(g, tile);
      drawWarmth(g);
      drawScouts(g);
      drawHover();
    }
    stepParticles(dt);
    drawParticles();
    drawSnow(dt, g);
  }

  const onResize = (): void => resize();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
  resize();

  return {
    resize,
    draw,
    hexSize: () => size,
    centreOf,

    cellAt(clientX: number, clientY: number) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width < 2) return null;
      // clientX/Y minus the rect, never offsetX/offsetY — the latter scales
      // oddly under DPR and zoom and silently mislands taps.
      const x = clientX - rect.left - ox;
      const y = clientY - rect.top - oy;
      const cell = fromPixel(x, y, size);
      const g = getGame();
      if (g && !g.at(cell.q, cell.r)) return null;
      return cell;
    },

    shake(power: number): void {
      shakeAmt = Math.min(18, shakeAmt + power);
    },

    pulse(inward: boolean): void {
      ringPulse = 1;
      ringPulseDir = inward ? -1 : 1;
    },

    puff(q: number, r: number, colour: string, count = 10): void {
      const c = centreOf(q, r);
      const n = soft ? Math.ceil(count / 3) : count;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 30 + Math.random() * 90;
        particles.push({
          x: c.x,
          y: c.y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - 30,
          life: 0.5 + Math.random() * 0.4,
          maxLife: 0.9,
          size: 1.5 + Math.random() * 2.5,
          colour,
        });
      }
    },

    float(q: number, r: number, text: string, colour: string): void {
      const c = centreOf(q, r);
      particles.push({
        x: c.x,
        y: c.y - size * 0.3,
        vx: 0,
        vy: -34,
        life: 1.1,
        maxLife: 1.1,
        size: Math.max(11, size * 0.5),
        colour,
        text,
      });
    },

    setHover(cell, ok): void {
      hover = cell;
      hoverOk = ok;
    },

    destroy(): void {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      particles.length = 0;
      flakes.length = 0;
    },
  };
}
